/**
 * Image Optimization Utilities
 * 
 * Client-side image optimization before upload to reduce file sizes
 * and improve loading performance.
 */

/**
 * Image optimization options
 */
export interface ImageOptimizationOptions {
  /** Maximum width in pixels */
  maxWidth?: number;
  /** Maximum height in pixels */
  maxHeight?: number;
  /** Quality (0-1) */
  quality?: number;
  /** Output format */
  format?: 'webp' | 'jpeg' | 'png';
  /** Maintain aspect ratio */
  maintainAspectRatio?: boolean;
}

/**
 * Default optimization presets
 */
export const IMAGE_PRESETS = {
  /** Full size hero images */
  hero: {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.85,
    format: 'webp' as const,
  },
  /** Standard content images */
  content: {
    maxWidth: 1200,
    maxHeight: 800,
    quality: 0.8,
    format: 'webp' as const,
  },
  /** Gallery photos */
  gallery: {
    maxWidth: 1600,
    maxHeight: 1200,
    quality: 0.8,
    format: 'webp' as const,
  },
  /** Thumbnail images */
  thumbnail: {
    maxWidth: 400,
    maxHeight: 300,
    quality: 0.7,
    format: 'webp' as const,
  },
  /** Small thumbnails */
  thumbnailSmall: {
    maxWidth: 200,
    maxHeight: 200,
    quality: 0.6,
    format: 'webp' as const,
  },
  /** Profile/Avatar images */
  avatar: {
    maxWidth: 200,
    maxHeight: 200,
    quality: 0.8,
    format: 'webp' as const,
  },
  /** Logo images */
  logo: {
    maxWidth: 400,
    maxHeight: 200,
    quality: 0.9,
    format: 'png' as const,
  },
} as const;

/**
 * Optimize an image file
 * 
 * @param file - Original image file
 * @param options - Optimization options
 * @returns Optimized image as Blob
 */
export const optimizeImage = async (
  file: File,
  options: ImageOptimizationOptions = {}
): Promise<Blob> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    format = 'webp',
    maintainAspectRatio = true,
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate new dimensions
        const { width, height } = calculateDimensions(
          img.width,
          img.height,
          maxWidth,
          maxHeight,
          maintainAspectRatio
        );

        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Use better image smoothing for downscaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        const mimeType = getMimeType(format);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          mimeType,
          quality
        );
      } catch (error) {
        reject(error);
      } finally {
        // Clean up
        URL.revokeObjectURL(img.src);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };

    // Load image from file
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Generate a thumbnail from an image file
 */
export const generateThumbnail = (
  file: File,
  size: 'small' | 'medium' = 'medium'
): Promise<Blob> => {
  const preset = size === 'small' ? IMAGE_PRESETS.thumbnailSmall : IMAGE_PRESETS.thumbnail;
  return optimizeImage(file, preset);
};

/**
 * Optimize image for specific use case
 */
export const optimizeForPreset = (
  file: File,
  preset: keyof typeof IMAGE_PRESETS
): Promise<Blob> => {
  return optimizeImage(file, IMAGE_PRESETS[preset]);
};

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
const calculateDimensions = (
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number,
  maintainAspectRatio: boolean
): { width: number; height: number } => {
  if (!maintainAspectRatio) {
    return {
      width: Math.min(originalWidth, maxWidth),
      height: Math.min(originalHeight, maxHeight),
    };
  }

  let width = originalWidth;
  let height = originalHeight;

  // Scale down if needed
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }

  if (height > maxHeight) {
    width = Math.round((width * maxHeight) / height);
    height = maxHeight;
  }

  return { width, height };
};

/**
 * Get MIME type for format
 */
const getMimeType = (format: 'webp' | 'jpeg' | 'png'): string => {
  switch (format) {
    case 'webp':
      return 'image/webp';
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    default:
      return 'image/webp';
  }
};

/**
 * Check if browser supports WebP
 */
export const supportsWebP = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
};

/**
 * Get optimized format for browser
 */
export const getOptimalFormat = async (): Promise<'webp' | 'jpeg'> => {
  const webpSupported = await supportsWebP();
  return webpSupported ? 'webp' : 'jpeg';
};

/**
 * Estimate file size after optimization
 * (Rough estimate based on quality and dimensions)
 */
export const estimateOptimizedSize = (
  originalSize: number,
  originalWidth: number,
  originalHeight: number,
  targetWidth: number,
  targetHeight: number,
  quality: number
): number => {
  const dimensionRatio = (targetWidth * targetHeight) / (originalWidth * originalHeight);
  const qualityFactor = quality * 0.8; // Assume ~80% of quality translates to size
  
  // WebP typically achieves 25-35% smaller files than JPEG at similar quality
  const formatFactor = 0.7;
  
  return Math.round(originalSize * dimensionRatio * qualityFactor * formatFactor);
};

/**
 * Validate image before optimization
 */
export const validateImage = (file: File): { valid: boolean; error?: string } => {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'File is not an image' };
  }

  // Check supported formats
  const supportedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!supportedFormats.includes(file.type)) {
    return { valid: false, error: `Unsupported image format: ${file.type}` };
  }

  // Check file size (50MB max for source images)
  const maxSize = 50 * 1024 * 1024;
  if (file.size > maxSize) {
    return { valid: false, error: 'Image file is too large (max 50MB)' };
  }

  return { valid: true };
};

export default {
  optimizeImage,
  generateThumbnail,
  optimizeForPreset,
  supportsWebP,
  getOptimalFormat,
  validateImage,
  IMAGE_PRESETS,
};





